/**
 * User List Component
 * Sortable table showing all free tier users
 */

import React, { useState, useMemo } from 'react';
import { Users, ChevronUp, ChevronDown, ExternalLink } from 'lucide-react';
import { FreeTierUser, USECASE_LABELS } from '../types';

interface UserListProps {
  users: FreeTierUser[];
  onUserSelect: (userId: number) => void;
}

type SortField =
  | 'name'
  | 'email'
  | 'signup_date'
  | 'simulation_count'
  | 'chat_session_count'
  | 'last_activity';
type SortDirection = 'asc' | 'desc';

const INITIAL_DISPLAY_COUNT = 5;

export const UserList: React.FC<UserListProps> = ({ users, onUserSelect }) => {
  const [sortField, setSortField] = useState<SortField>('signup_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showAll, setShowAll] = useState(false);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (aValue === null) aValue = sortDirection === 'asc' ? Infinity : -Infinity;
      if (bValue === null) bValue = sortDirection === 'asc' ? Infinity : -Infinity;

      if (sortField === 'signup_date' || sortField === 'last_activity') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue as string).toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatUseCase = (usecase: string | null): string => {
    if (!usecase) return 'N/A';
    return (
      USECASE_LABELS[usecase] || usecase.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100">
        <Users className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Free Tier Users</h3>
        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
          {users.length}
        </span>
      </div>

      {users.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No free tier users found</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  User <SortIcon field="name" />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('signup_date')}
                >
                  Signed Up <SortIcon field="signup_date" />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('simulation_count')}
                >
                  Simulations <SortIcon field="simulation_count" />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('chat_session_count')}
                >
                  Chats <SortIcon field="chat_session_count" />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('last_activity')}
                >
                  Last Activity <SortIcon field="last_activity" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Primary Use Case
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(showAll ? sortedUsers : sortedUsers.slice(0, INITIAL_DISPLAY_COUNT)).map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.name || 'Unnamed'}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">{formatDate(user.signup_date)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono font-semibold text-gray-900">
                      {user.simulation_count}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono font-semibold text-gray-900">
                      {user.chat_session_count}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">
                      {formatDateTime(user.last_activity)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {formatUseCase(user.primary_usecase)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => onUserSelect(user.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      View Details
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* See More / Show Less */}
      {users.length > INITIAL_DISPLAY_COUNT && (
        <div className="px-6 py-3 border-t border-gray-100">
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showAll
              ? `Show Less`
              : `See More (${users.length - INITIAL_DISPLAY_COUNT} more users)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserList;
